// Utility for parsing timestamps from comment text
// Supports formats like: HH:MM:SS, MM:SS, H:MM:SS, M:SS, etc.

import React from 'react';

export interface ParsedTimestamp {
  originalText: string;
  seconds: number;
  formattedTime: string;
}

// Parse timestamp strings like "1:23:45", "23:45", "1:05", etc.
export const parseTimestamp = (text: string): ParsedTimestamp | null => {
  // Match various timestamp formats
  const timestampRegex = /\b(\d{1,2}):(\d{2})(?::(\d{2}))?\b/g;
  const match = timestampRegex.exec(text);
  
  if (!match) return null;
  
  const originalText = match[0];
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  
  if (match[3]) {
    // HH:MM:SS format
    hours = parseInt(match[1], 10);
    minutes = parseInt(match[2], 10);
    seconds = parseInt(match[3], 10);
  } else {
    // MM:SS format
    minutes = parseInt(match[1], 10);
    seconds = parseInt(match[2], 10);
  }
  
  // Validate the parsed values
  if (minutes >= 60 || seconds >= 60) return null;
  
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;
  
  // Format the time consistently
  const formattedTime = hours > 0
    ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    : `${minutes}:${seconds.toString().padStart(2, '0')}`;
  
  return {
    originalText,
    seconds: totalSeconds,
    formattedTime
  };
};

// Extract all timestamps from a text
export const extractTimestamps = (text: string): ParsedTimestamp[] => {
  const timestamps: ParsedTimestamp[] = [];
  const timestampRegex = /\b(\d{1,2}):(\d{2})(?::(\d{2}))?\b/g;
  let match;
  
  while ((match = timestampRegex.exec(text)) !== null) {
    const originalText = match[0];
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    
    if (match[3]) {
      hours = parseInt(match[1], 10);
      minutes = parseInt(match[2], 10);
      seconds = parseInt(match[3], 10);
    } else {
      minutes = parseInt(match[1], 10);
      seconds = parseInt(match[2], 10);
    }
    
    if (minutes < 60 && seconds < 60) {
      const totalSeconds = hours * 3600 + minutes * 60 + seconds;
      const formattedTime = hours > 0
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        : `${minutes}:${seconds.toString().padStart(2, '0')}`;
      
      timestamps.push({
        originalText,
        seconds: totalSeconds,
        formattedTime
      });
    }
  }
  
  return timestamps;
};

// Replace timestamps in text with clickable links
export const replaceTimestampsWithLinks = (
  text: string,
  onTimestampClick: (seconds: number) => void
): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const timestampRegex = /\b(\d{1,2}):(\d{2})(?::(\d{2}))?\b/g;
  let match;
  
  while ((match = timestampRegex.exec(text)) !== null) {
    // Add text before the timestamp
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    const timestamp = parseTimestamp(match[0]);
    if (timestamp) {
      // Add the clickable timestamp
      parts.push(
        <button
          key={match.index}
          onClick={() => onTimestampClick(timestamp.seconds)}
          className="text-blue-500 hover:text-blue-700 underline font-mono text-sm"
          title={`Jump to ${timestamp.formattedTime}`}
        >
          {timestamp.originalText}
        </button>
      );
    } else {
      // If parsing failed, just add the text
      parts.push(match[0]);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : [text];
};

// Format seconds to display time
export const formatSecondsToTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};